import { FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { resolveCountryCode } from "@/lib/sign-up-countries";
import { useSignUpCountries } from "@/features/auth/api/get-countries";
import { useCallback, useEffect, useMemo } from "react";
import type { ControllerRenderProps, FieldValues } from "react-hook-form";

import { SignUpCountrySelect } from "./sign-up-country-select";

type Props = {
  field: ControllerRenderProps<FieldValues, string>;
  label: string;
  required: boolean;
  readOnly?: boolean;
  showFirst?: string[];
};

export const SignUpCountryField = ({ field, label, required, readOnly, showFirst }: Props) => {

  const { countriesMap, countryOptions, isPending } = useSignUpCountries({ showFirst });
  const map = useMemo(() => countriesMap ?? {}, [countriesMap]);

  const raw = typeof field.value === "string" ? field.value : "";
  const code = resolveCountryCode(map, raw.trim() || undefined);

  useEffect(() => {
    if (readOnly) return;
    if (!countriesMap || Object.keys(countriesMap).length === 0) return;
    const resolved = resolveCountryCode(countriesMap, raw.trim() || undefined);
    if (!resolved || resolved === raw.trim()) return;
    field.onChange(resolved);
  }, [raw, field, countriesMap, readOnly]);

  const onPick = useCallback(
    (isoCodeOrEmpty: string) => {
      if (readOnly) return;
      field.onChange(isoCodeOrEmpty);
    },
    [field, readOnly]
  );

  return (
    <FormItem>
      <FormLabel>
        {label}
        {required ? " *" : ""}
      </FormLabel>
      <FormControl>
        <SignUpCountrySelect
          required={required}
          valueCode={code}
          onPick={onPick}
          ariaLabel={label}
          options={countryOptions}
          optionsLoading={isPending}
          disabled={readOnly}
        />
      </FormControl>
      <FormMessage />
    </FormItem>
  );
};
